import { schemaResolver, useForm } from '@mantine/form';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { tKeysLoose, useTranslation } from 'locales';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { hasAtLeast, isNonNullish } from 'remeda';
import {
  ContactPersonFormSchema,
  type CreateContactPersonForm,
} from 'shared/hooks/use-create-contact-person';
import { type ContactPerson, useLoadContactPerson } from 'shared/hooks/use-load-contact-person';
import { NOTIFICATION_TYPES } from 'types/notification-types';

export type ContactPersonFormParams = {
  id: string;
  contactPersonId?: string;
};

const DEFAULT_ADDRESS = {
  id: '',
  street_name: '',
  street_number: '',
  street_box: '',
  zip_code: '',
  city: '',
  country_code: null,
  additional_line_1: '',
  additional_line_2: '',
  country: '',
};

const DEFAULT_CONTACT_PERSON: CreateContactPersonForm = {
  first_name: '',
  last_name: '',
  job_title: '',
  locale: null,
  main_contact: false,
  emails: [],
  phones: [{ number: '' }],
  notifications: [],
  address: { ...DEFAULT_ADDRESS },
  use_postal_address: false,
};

const initializePhones = (contactPerson: ContactPerson) => {
  if (hasAtLeast(contactPerson.phones, 1)) {
    return contactPerson.phones.map(phone => ({
      id: phone.id?.toString(),
      number: phone.number,
    }));
  }
  return [{ number: '' }];
};

const initializeAddress = (contactPerson: ContactPerson) => {
  if (contactPerson?.address && Object.values(contactPerson.address).some(value => value)) {
    return contactPerson.address;
  }
  return { ...DEFAULT_ADDRESS };
};

export const useContactPersonForm = () => {
  const { t, currentLang } = useTranslation();
  const company = useCompanyConfiguration();

  const { id: debtorId, contactPersonId } = useParams<ContactPersonFormParams>();
  const isEditMode = isNonNullish(contactPersonId);

  const { contactPerson, isContactPersonInitialLoading } = useLoadContactPerson({
    debtorId,
    contactPersonId,
    isEditMode,
  });

  const getInitialValues = () => {
    const baseValues = isEditMode ? contactPerson : DEFAULT_CONTACT_PERSON;
    return {
      ...baseValues,
      phones: initializePhones(contactPerson),
      address: initializeAddress(contactPerson),
    };
  };

  const form = useForm<CreateContactPersonForm>({
    initialValues: getInitialValues(),
    validate: schemaResolver(ContactPersonFormSchema, { sync: true }),
  });

  useEffect(() => {
    if (isEditMode) {
      form.setValues(getInitialValues());
      form.resetDirty();
    }
  }, [contactPerson, isEditMode]);

  const getNotificationsItems = () =>
    NOTIFICATION_TYPES.filter(value => value !== 'ai_call' || company.package.can_use_ai_calls).map(
      value => ({
        description: t(tKeysLoose.CONTACT_PERSON.NOTIFICATIONS[value.toUpperCase()]),
        value,
      }),
    );

  return {
    form,
    isEditMode,
    isContactPersonInitialLoading,
    getNotificationsItems,
    currentLang,
  };
};
